import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Switch,
  TextField,
  Alert,
  Box,
  Typography,
  Chip,
  CircularProgress,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { DialogTitleWithClose } from '../../common';

/**
 * EditAssignmentDialog Component
 *
 * Dialog for editing an existing policy assignment.
 * Allows updating enabled status only. Policy configuration is managed in Settings.
 */
const EditAssignmentDialog = ({ open, onClose, onUpdate, assignment }) => {
  const [enabled, setEnabled] = useState(true);
  const [changeReason, setChangeReason] = useState('');
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  // Initialize form with assignment data
  useEffect(() => {
    if (open && assignment) {
      setEnabled(assignment.enabled !== false);
      setChangeReason('');
      setError(null);
    }
  }, [open, assignment]);

  const handleUpdate = async () => {
    try {
      setSubmitting(true);
      setError(null);

      await onUpdate(assignment.assignment_id || assignment.id, {
        enabled,
        change_reason: changeReason.trim() || null,
      });

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update assignment');
    } finally {
      setSubmitting(false);
    }
  };

  if (!assignment) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitleWithClose
        title={
          <Box>
            <Box display="flex" alignItems="center" gap={1}>
              <EditIcon />
              <Typography variant="h6">Edit Policy Assignment</Typography>
            </Box>
            {assignment.policy_name && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {assignment.policy_name}
              </Typography>
            )}
          </Box>
        }
        onClose={onClose}
        disableClose={submitting}
      />

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Policy Info (Read-only) */}
        <Box
          sx={{ mb: 3, p: 2, bgcolor: 'background.default', borderRadius: 1 }}
        >
          <Typography variant="subtitle2" gutterBottom>
            Policy Information
          </Typography>
          <Typography variant="body1" fontWeight={500}>
            {assignment.policy_name || 'Unknown Policy'}
          </Typography>
          {assignment.policy_domains &&
            assignment.policy_domains.length > 0 && (
              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1 }}>
                {assignment.policy_domains.map(domain => (
                  <Chip
                    key={domain}
                    label={domain.toUpperCase()}
                    size="small"
                  />
                ))}
              </Box>
            )}
        </Box>

        {/* Enabled Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={enabled}
              onChange={e => setEnabled(e.target.checked)}
            />
          }
          label="Enable this assignment"
          sx={{ mb: 3 }}
        />

        {/* Change Reason */}
        <TextField
          fullWidth
          label="Reason for Change (Optional)"
          value={changeReason}
          onChange={e => setChangeReason(e.target.value)}
          placeholder="e.g., 'Enabling policy for compliance requirements'"
          sx={{ mb: 3 }}
          helperText="This reason will be recorded in the assignment history"
        />

        <Alert severity="info" sx={{ mt: 2 }}>
          The policy configuration is managed in Settings. You can only enable
          or disable the assignment here.
        </Alert>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleUpdate}
          variant="contained"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={20} /> : <EditIcon />}
        >
          {submitting ? 'Updating...' : 'Update Assignment'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditAssignmentDialog;
